import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import MetaData from "../layouts/MetaData";
import { createNewUser } from "../../actions/userActions";
import { clearUserCreated, clearError } from "../../slices/userSlice";

export default function RegisterUser() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { loading, isUserCreated, error } = useSelector((state) => state.userState);
    const { user: currentUser } = useSelector((state) => state.authState);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [role, setRole] = useState("admin");
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (currentUser && currentUser.role !== "master") {
            toast.error("Access denied. Master Admin access only.");
            navigate("/dashboard");
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        dispatch(clearUserCreated());
        dispatch(clearError());
        return () => {
            dispatch(clearUserCreated());
            dispatch(clearError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isUserCreated) {
            toast("User registered successfully!", { type: "success" });
            dispatch(clearUserCreated());
            navigate("/admin/users");
        }
        if (error) {
            toast(error, { type: "error" });
            dispatch(clearError());
        }
    }, [isUserCreated, error, dispatch, navigate]);

    const submitHandler = (e) => {
        e.preventDefault();

        if (!name.trim() || !email.trim() || !password) {
            return toast.error("Please fill in all required fields");
        }

        if (password.length < 6) {
            return toast.error("Password must be at least 6 characters long");
        }

        if (password !== confirmPassword) {
            return toast.error("Passwords do not match");
        }

        dispatch(createNewUser({
            name: name.trim(),
            email: email.trim().toLowerCase(),
            password,
            role
        }));
    };

    const getRoleDescription = (selectedRole) => {
        switch (selectedRole) {
            case "admin":
                return {
                    title: "Administrator",
                    badge: "bg-primary",
                    desc: "Full operational access across the clinic system (Patients, Staff, Attendance, Billing, Accounts, Trash). Cannot add/edit users or change passwords (reserved for Master).",
                    icon: "fa-user-shield"
                };
            case "manager":
                return {
                    title: "Manager",
                    badge: "bg-info text-dark",
                    desc: "Management access to Patients, Staff, Attendance, Billing, and Accounts overview.",
                    icon: "fa-briefcase"
                };
            case "doctor":
                return {
                    title: "Doctor",
                    badge: "bg-success",
                    desc: "Medical access to view patients, manage daily visit treatments, and view records.",
                    icon: "fa-user-md"
                };
            default:
                return {
                    title: "Standard User / Staff",
                    badge: "bg-secondary",
                    desc: "Standard staff access to view patients and general hospital workflow.",
                    icon: "fa-user"
                };
        }
    };

    const roleInfo = getRoleDescription(role);

    return (
        <Fragment>
            <MetaData title="Register User" />
            <div className="row g-0">
                <div className="col-12 col-md-2"><Sidebar /></div>
                <div className="col-12 col-md-10 main-content">
                    <div className="container my-4" style={{ maxWidth: "720px" }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h2 className="fw-bold mb-1" style={{ fontSize: "1.5rem" }}>
                                    <i className="fa fa-user-plus me-2 text-primary"></i>Register User / Admin
                                </h2>
                                <p className="text-muted small mb-0">
                                    Create a new staff or admin user account (Master Admin only)
                                </p>
                            </div>
                            <Link to="/admin/users" className="btn btn-sm btn-outline-secondary">
                                <i className="fa fa-arrow-left me-1"></i> Back to Users
                            </Link>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="card-body p-4">
                                <form onSubmit={submitHandler}>
                                    <div className="form-group mb-3">
                                        <label className="form-label fw-semibold">
                                            Full Name <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light">
                                                <i className="fa fa-user text-muted"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="e.g. Dr. Ramesh Kumar"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="form-label fw-semibold">
                                            Email Address <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light">
                                                <i className="fa fa-envelope text-muted"></i>
                                            </span>
                                            <input
                                                type="email"
                                                className="form-control"
                                                placeholder="e.g. ramesh@greennaturopathy.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <small className="text-muted">This will be used as their login username.</small>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">
                                                Password <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <i className="fa fa-lock text-muted"></i>
                                                </span>
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    className="form-control"
                                                    placeholder="Min. 6 characters"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    tabIndex="-1"
                                                >
                                                    <i className={`fa fa-eye${showPassword ? "-slash" : ""}`}></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">
                                                Confirm Password <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <i className="fa fa-lock text-muted"></i>
                                                </span>
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    className="form-control"
                                                    placeholder="Re-enter password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="form-label fw-semibold">
                                            Account Role <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select form-select-lg"
                                            value={role}
                                            onChange={(e) => setRole(e.target.value)}
                                            style={{ fontSize: "0.95rem" }}
                                        >
                                            <option value="admin">Administrator (Admin)</option>
                                            <option value="doctor">Doctor</option>
                                            <option value="manager">Manager</option>
                                            <option value="user">Staff / User</option>
                                        </select>
                                    </div>

                                    {/* Role Description Card */}
                                    <div className="card bg-light border-0 p-3 mb-4 rounded-3">
                                        <div className="d-flex align-items-center mb-1">
                                            <span className={`badge ${roleInfo.badge} me-2`}>
                                                <i className={`fa ${roleInfo.icon} me-1`}></i>
                                                {roleInfo.title}
                                            </span>
                                            <small className="text-muted fw-semibold">Permissions Profile</small>
                                        </div>
                                        <p className="mb-0 small text-secondary">
                                            {roleInfo.desc}
                                        </p>
                                    </div>

                                    <div className="d-flex justify-content-end gap-2">
                                        <Link to="/admin/users" className="btn btn-secondary px-4">
                                            Cancel
                                        </Link>
                                        <button
                                            type="submit"
                                            className="btn btn-primary px-4 shadow-sm"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <Fragment>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Creating Account...
                                                </Fragment>
                                            ) : (
                                                <Fragment>
                                                    <i className="fa fa-check me-2"></i> Register User
                                                </Fragment>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}
